import React, {
  useState,
  useEffect,
  useCallback,
  FunctionComponent,
  Fragment,
} from "react";
import { Row, Col, Button, Collapse, Select, Radio, Tag, Spin, Space } from "antd";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import fetchProductSummary, {
  FetchProductSummary,
} from "../../redux/actions/reports/fetchProductSummary";
import fetchAccountOwnerSummary, {
  FetchAccountOwnerSummary,
} from "../../redux/actions/reports/fetchAccountOwnerSummary";
import fetchXtdByGroup, {
  FetchXtdByGroup,
} from "../../redux/actions/reports/fetchXtdByGroup";
import ProductTable from "./ProductTable";
import AccountTable from "./AccountTable";
import GroupTable from "./GroupTable";
import PartnerTable from "./PartnerTable";
import {
  ProductSummaryRecord,
  AccountOwnerSummaryRecord,
  XtdByGroupRecord,
  XtdByPubRecord,
} from "../../redux/types/reportTypes";
import { StoreType } from "../../redux/store";
import "./index.scss";
import fetchXtdByPub, { FetchXtdByPub } from "../../redux/actions/reports/fetchXtdByPub";
import AccountCSV from "./AccountCSV";
import ProductCSV from "./ProductCSV";
import PartnerCSV from "./PartnerCSV";
import GroupCSV from "./GroupCSV";

type RecordOptions =
  | ProductSummaryRecord
  | AccountOwnerSummaryRecord
  | XtdByGroupRecord
  | XtdByPubRecord;
export const rowClassName = (record: RecordOptions): string => {
  let className = "";
  if (record.wtd_diff < 0) className += "down-wtd-diff";
  else if (record.wtd_diff > 0) className += "up-wtd-diff";
  if (record.wtd_perc_diff < 0) className += " down-wtd-perc-diff";
  else if (record.wtd_perc_diff > 0) className += " up-wtd-perc-diff";
  if (record.mtd_diff < 0) className += "down-mtd-diff";
  else if (record.mtd_diff > 0) className += "up-mtd-diff";
  if (record.mtd_perc_diff < 0) className += " down-mtd-perc-diff";
  else if (record.mtd_perc_diff > 0) className += " up-mtd-perc-diff";
  if (record.qtd_diff < 0) className += "down-qtd-diff";
  else if (record.qtd_diff > 0) className += "up-qtd-diff";
  if (record.qtd_perc_diff < 0) className += " down-qtd-perc-diff";
  else if (record.qtd_perc_diff > 0) className += " up-qtd-perc-diff";
  return className;
};

type WtdMetQtdProps = {
  fetchProducts: FetchProductSummary;
  fetchAccounts: FetchAccountOwnerSummary;
  fetchGroups: FetchXtdByGroup;
  fetchPubs: FetchXtdByPub;
  productLoading: boolean;
  productRecords: Array<ProductSummaryRecord>;
  accountLoading: boolean;
  accountRecords: Array<AccountOwnerSummaryRecord>;
  groupLoading: boolean;
  groupRecords: Array<XtdByGroupRecord>;
  pubLoading: boolean;
  pubRecords: Array<XtdByPubRecord>;
};

export const WtdMetQtd: FunctionComponent<WtdMetQtdProps> = ({
  fetchProducts,
  fetchAccounts,
  fetchGroups,
  fetchPubs,
  productLoading,
  productRecords,
  accountLoading,
  accountRecords,
  groupLoading,
  groupRecords,
  pubLoading,
  pubRecords,
}: WtdMetQtdProps) => {
  const [clusters, setClusters] = useState<Array<string>>();
  const [partners, setPartners] = useState<Array<number>>();
  const [origami, setOrigami] = useState<string>();
  const [managers, setManagers] = useState<Array<string>>();

  const setInitialState = useCallback(() => {
    fetchProducts({});
    fetchAccounts({});
    fetchGroups({});
    fetchPubs({});
    setClusters([]);
    setPartners([]);
    setOrigami("");
    setManagers([]);
  }, [fetchProducts, fetchAccounts, fetchGroups, fetchPubs]);

  useEffect(() => {
    setInitialState();
  }, [setInitialState]);

  const onClick = () => {
    fetchProducts(filters());
    fetchAccounts(filters());
    fetchGroups(filters());
    fetchPubs(filters());
  };

  const filters = () => ({
    clusters,
    partners,
    origami,
    am: managers,
  });

  const clusterOptions = () =>
    groupRecords.map((record) => (
      <Select.Option key={record.cluster} value={record.cluster}>
        {record.cluster}
      </Select.Option>
    ));

  const clusterExtra = () => {
    if (groupLoading) return <Spin />;
    return (
      <Fragment>
        <Tag color="geekblue">{`${groupRecords.length} Result(s)`}</Tag>
        <GroupCSV data={groupRecords} />
      </Fragment>
    );
  };

  const partnerOptions = () =>
    pubRecords.map((record) => (
      <Select.Option key={record.publisher} value={record.publisher}>
        {record.name}
      </Select.Option>
    ));

  const partnerExtra = () => {
    if (pubLoading) return <Spin />;
    return (
      <Fragment>
        <Tag color="geekblue">{`${pubRecords.length} Result(s)`}</Tag>
        <PartnerCSV data={pubRecords} />
      </Fragment>
    );
  };

  const productExtra = () => {
    if (productLoading) return <Spin />;
    return (
      <Fragment>
        <Tag color="geekblue">{`${productRecords.length} Result(s)`}</Tag>
        <ProductCSV data={productRecords} />
      </Fragment>
    );
  };

  const accountExtra = () => {
    if (accountLoading) return <Spin />;
    return (
      <Fragment>
        <Tag color="geekblue">{`${accountRecords.length} Result(s)`}</Tag>
        <AccountCSV data={accountRecords} />
      </Fragment>
    );
  };

  const managerOptions = () =>
    accountRecords.map((record) => (
      <Select.Option key={record.manager} value={record.manager}>
        {record.manager}
      </Select.Option>
    ));

  return (
    <Space direction="vertical" className="wtd-mtd-qtd">
      <Row gutter={16}>
        <Col span={6}>
          <Radio.Group
            defaultValue=""
            buttonStyle="solid"
            value={origami}
            onChange={(event) => setOrigami(event.target.value)}
          >
            <Radio.Button value="">Both</Radio.Button>
            <Radio.Button value="0">Legacy</Radio.Button>
            <Radio.Button value="1">Origami</Radio.Button>
          </Radio.Group>
        </Col>
        <Col span={2}>
          <Button type="primary" onClick={onClick}>
            Filter
          </Button>
        </Col>
        <Col span={2}>
          <Button onClick={setInitialState}>Clear</Button>
        </Col>
      </Row>
      <Collapse expandIconPosition="right" className="filters-collapse">
        <Collapse.Panel header="Filters" key="1">
          <Row gutter={16} className="filters-container">
            <Col span={8}>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Select Clusters"
                onChange={(value) => setClusters(value)}
                value={clusters}
              >
                {clusterOptions()}
              </Select>
            </Col>
            <Col span={8}>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Select Partners"
                onChange={(value) => setPartners(value)}
                value={partners}
              >
                {partnerOptions()}
              </Select>
            </Col>
            <Col span={8}>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Select Managers"
                onChange={(value) => setManagers(value)}
                value={managers}
              >
                {managerOptions()}
              </Select>
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>
      <div className="accordion-container">
        <Collapse defaultActiveKey={["1", "2", "3", "4"]}>
          <Collapse.Panel header="Summary - Product" key="1" extra={productExtra()}>
            <ProductTable records={productRecords} loading={productLoading} />
          </Collapse.Panel>
          <Collapse.Panel header="Summary - Account Owner" key="2" extra={accountExtra()}>
            <AccountTable records={accountRecords} loading={accountLoading} />
          </Collapse.Panel>
          <Collapse.Panel header="WTD/MTD/QTD by Group" key="3" extra={clusterExtra()}>
            <GroupTable records={groupRecords} loading={groupLoading} />
          </Collapse.Panel>
          <Collapse.Panel header="WTD/MTD/QTD by Pub" key="4" extra={partnerExtra()}>
            <PartnerTable records={pubRecords} loading={pubLoading} />
          </Collapse.Panel>
        </Collapse>
      </div>
    </Space>
  );
};

const mapDispatchToProps = {
  fetchProducts: fetchProductSummary,
  fetchAccounts: fetchAccountOwnerSummary,
  fetchGroups: fetchXtdByGroup,
  fetchPubs: fetchXtdByPub,
};

const mapStateToProps = ({
  reports: {
    product_summary: { loading: productLoading, records: productRecords },
    account_owner_summary: { loading: accountLoading, records: accountRecords },
    xtd_by_group: { loading: groupLoading, records: groupRecords },
    xtd_by_pub: { loading: pubLoading, records: pubRecords },
  },
}: StoreType) => ({
  productLoading,
  productRecords,
  accountLoading,
  accountRecords,
  groupLoading,
  groupRecords,
  pubLoading,
  pubRecords,
});

WtdMetQtd.propTypes = {
  fetchProducts: PropTypes.func.isRequired,
  productLoading: PropTypes.bool.isRequired,
  productRecords: PropTypes.array.isRequired,
  fetchAccounts: PropTypes.func.isRequired,
  accountLoading: PropTypes.bool.isRequired,
  accountRecords: PropTypes.array.isRequired,
  fetchGroups: PropTypes.func.isRequired,
  groupLoading: PropTypes.bool.isRequired,
  groupRecords: PropTypes.array.isRequired,
  fetchPubs: PropTypes.func.isRequired,
  pubLoading: PropTypes.bool.isRequired,
  pubRecords: PropTypes.array.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(WtdMetQtd);
